import { useEffect, useState, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  Briefcase, FileText, Check, Loader2, Bot, ChevronRight, AlertCircle,
  Calendar as CalIcon, MessageSquare, Send, Sparkles, ArrowLeft, Clock,
  CheckCircle2, Circle, Target, FileSignature, Receipt, FolderOpen, User, X
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import PageHeader from "../components/PageHeader";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AgentChat } from "../components/AgentChat";

const inputCls = "bg-[#161020] border-white/10 text-white placeholder:text-slate-500";
const STATUS_CFG = {
  active: { label: "Actif", cls: "bg-green-500/10 text-green-400 border-green-500/20" },
  en_pause: { label: "En pause", cls: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20" },
  termine: { label: "Termine", cls: "bg-slate-500/10 text-slate-400 border-slate-500/20" },
};

export default function ServicesPage() {
  const { axiosAuth, user } = useAuth();
  const api = axiosAuth();
  const [services, setServices] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [docTemplates, setDocTemplates] = useState([]);
  const [intakeForms, setIntakeForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAgent, setShowAgent] = useState(false);
  const [selected, setSelected] = useState(null); // service_id — detail view

  useEffect(() => {
    Promise.all([
      api.get("/services").then(r => setServices(r.data || [])),
      api.get("/services/my/reminders").then(r => setReminders(r.data?.items || [])).catch(() => {}),
      api.get("/catalog/document-templates").then(r => setDocTemplates(r.data || [])).catch(() => {}),
      api.get("/intake-forms").then(r => setIntakeForms(r.data || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const refresh = async () => {
    const [a, b] = await Promise.all([
      api.get("/services").then(r => r.data || []),
      api.get("/intake-forms").then(r => r.data || []).catch(() => []),
    ]);
    setServices(a);
    setIntakeForms(b);
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div>;

  if (selected) {
    return <ServiceDetail
      serviceId={selected}
      api={api}
      user={user}
      docTemplates={docTemplates}
      intakeForms={intakeForms}
      onBack={() => { setSelected(null); refresh(); }}
      onAgentToggle={() => setShowAgent(!showAgent)}
      agentVisible={showAgent}
    />;
  }

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="services-page">
      <PageHeader
        icon={<Sparkles />}
        title="Mes Services"
        description="Retrouvez l'ensemble de vos prestations Blitzz, suivez leur avancement, échangez avec votre consultant et recevez vos livrables."
        testId="services-header"
        actions={(
          <button onClick={() => setShowAgent(!showAgent)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${showAgent ? "bg-[#f7c935] text-[#14101c]" : "bg-[#14101c] border border-[#f7c935]/30 text-[#f7c935] hover:bg-[#f7c935]/10"}`}
            data-testid="floating-chat-btn">
            <Bot className="w-4 h-4" /> Expert Stratégie
          </button>
        )}
      />

      {/* Reminders banner */}
      {reminders.length > 0 && (
        <div className="bg-gradient-to-r from-red-500/10 to-[#f7c935]/10 border border-red-500/20 rounded-xl p-4 mb-6" data-testid="reminders-banner">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-4 h-4 text-red-400" />
            <span className="text-sm font-bold text-white">{reminders.length} element{reminders.length > 1 ? "s" : ""} necessite{reminders.length > 1 ? "nt" : ""} votre attention</span>
          </div>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {reminders.slice(0, 5).map((r, i) => (
              <button key={i} onClick={() => setSelected(r.service_id)} className="w-full text-left flex items-center gap-2 text-xs text-slate-300 hover:text-white p-1.5 rounded hover:bg-white/5" data-testid={`reminder-${i}`}>
                <Circle className="w-2 h-2 text-red-400 fill-red-400 flex-shrink-0" />
                <span className="font-semibold">{r.service_name}</span>
                <span className="text-slate-500">— {r.message}</span>
                {r.due_date && <span className="ml-auto text-red-400">{r.due_date}</span>}
                <ChevronRight className="w-3 h-3 text-slate-500" />
              </button>
            ))}
          </div>
        </div>
      )}

      {services.length === 0 ? (
        <div className="bg-[#14101c] border border-white/10 rounded-xl p-12 text-center">
          <Briefcase className="w-12 h-12 mx-auto mb-4 text-slate-600" />
          <h3 className="text-xl font-bold mb-2">Aucun service</h3>
          <p className="text-slate-500">Vos services apparaitront ici une fois assignes par l'equipe Blitzz.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((srv, i) => {
            const intake = intakeForms.find(f => f.service_id === srv.service_id);
            const phases = srv.phases || [];
            const done = phases.filter(p => p.status === "done").length;
            const progress = phases.length ? Math.round((done / phases.length) * 100) : 0;
            const statusCfg = STATUS_CFG[srv.status] || STATUS_CFG.active;
            const needsIntake = srv.intake_form_template_id && !intake;
            return (
              <button key={srv.service_id} onClick={() => setSelected(srv.service_id)}
                className="text-left bg-gradient-to-br from-[#14101c] to-[#0b0811] border border-white/10 hover:border-[#f7c935]/30 rounded-xl p-5 transition-all animate-fade-in-up group"
                style={{ animationDelay: `${i * 0.05}s` }}
                data-testid={`service-card-${srv.service_id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "#461d8a25" }}>
                    <Briefcase className="w-5 h-5" style={{ color: "#a78bfa" }} />
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${statusCfg.cls}`}>{statusCfg.label}</span>
                </div>
                <h3 className="text-base font-bold mb-1 group-hover:text-[#f7c935] transition-colors">{srv.name}</h3>
                <p className="text-xs text-slate-500 mb-3 line-clamp-2">{srv.description || "Pas de description"}</p>

                {/* Progress bar */}
                {phases.length > 0 && (
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">Avancement</span>
                      <span className="text-[11px] font-bold text-[#f7c935]">{progress}%</span>
                    </div>
                    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#f7c935] to-[#a78bfa] transition-all" style={{ width: `${progress}%` }} />
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1">{done}/{phases.length} phases terminees</div>
                  </div>
                )}

                {/* Meta */}
                <div className="flex items-center gap-2 flex-wrap text-[10px] text-slate-500 mb-3">
                  {srv.category && <span className="px-2 py-0.5 rounded bg-white/5">{srv.category}</span>}
                  {srv.price && <span className="font-bold" style={{ color: "#f7c935" }}>{srv.price}</span>}
                  {srv.last_comment_at && srv.last_comment_author !== user?.role && (
                    <span className="flex items-center gap-1 text-blue-400"><MessageSquare className="w-2.5 h-2.5" /> Nouveau message</span>
                  )}
                </div>

                {/* Alert indicator */}
                {needsIntake && (
                  <div className="mt-3 pt-3 border-t border-red-500/10 flex items-center gap-2 text-[11px] text-red-400" data-testid={`needs-intake-${srv.service_id}`}>
                    <AlertCircle className="w-3.5 h-3.5" />
                    <span>Document requis a remplir</span>
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between text-[11px] text-slate-400 group-hover:text-[#f7c935] transition-colors">
                  <span>Ouvrir le parcours</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {showAgent && (
        <div className="fixed bottom-6 right-6 w-[420px] z-50 shadow-2xl shadow-black/50" data-testid="floating-chat-widget">
          <AgentChat agentType="conseiller" agentName="Expert Strategie" agentColor="#f7c935"
            context={{}} onClose={() => setShowAgent(false)} />
        </div>
      )}
    </div>
  );
}


// ─── SERVICE DETAIL VIEW ───────────────────────────────────────
function ServiceDetail({ serviceId, api, user, docTemplates, intakeForms, onBack, onAgentToggle, agentVisible }) {
  const [bundle, setBundle] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("timeline");
  const [newComment, setNewComment] = useState("");
  const [sending, setSending] = useState(false);

  // Intake form
  const [showForm, setShowForm] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [formResponses, setFormResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [viewingForm, setViewingForm] = useState(null);

  const commentsEndRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [bRes, cRes] = await Promise.all([
        api.get(`/services/${serviceId}/bundle`),
        api.get(`/services/${serviceId}/comments`).catch(() => ({ data: [] })),
      ]);
      setBundle(bRes.data);
      setComments(cRes.data || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, [serviceId]);

  useEffect(() => {
    if (tab === "comments") {
      setTimeout(() => commentsEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    }
  }, [tab, comments.length]);

  const postComment = async () => {
    if (!newComment.trim()) return;
    setSending(true);
    try {
      await api.post(`/services/${serviceId}/comments`, { body: newComment });
      setNewComment("");
      const r = await api.get(`/services/${serviceId}/comments`);
      setComments(r.data || []);
    } catch (e) { console.error(e); }
    setSending(false);
  };

  const openIntake = () => {
    const svc = bundle?.service;
    if (!svc?.intake_form_template_id) return;
    const tpl = docTemplates.find(t => t.template_id === svc.intake_form_template_id);
    if (!tpl) return alert("Template d'intake non disponible.");
    setActiveTemplate(tpl);
    setFormResponses({});
    setShowForm(true);
  };

  const submitIntake = async () => {
    setSubmitting(true);
    try {
      await api.post("/intake-forms", {
        service_id: serviceId, client_id: user?.user_id,
        template_id: activeTemplate.template_id, template_name: activeTemplate.name,
        responses: formResponses,
      });
      setShowForm(false);
      await load();
    } catch (e) { console.error(e); alert("Erreur lors de la soumission."); }
    setSubmitting(false);
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div>;
  if (!bundle) return <div className="p-8 text-slate-400">Service introuvable</div>;

  const svc = bundle.service;
  const statusCfg = STATUS_CFG[svc.status] || STATUS_CFG.active;
  const phases = svc.phases || [];
  const existingIntake = intakeForms.find(f => f.service_id === serviceId);
  const progress = bundle.progress || 0;

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid={`service-detail-${serviceId}`}>
      {/* Header */}
      <button onClick={onBack} className="text-xs text-slate-400 hover:text-white flex items-center gap-1 mb-4" data-testid="service-back-btn">
        <ArrowLeft className="w-3.5 h-3.5" /> Retour aux services
      </button>

      <div className="bg-gradient-to-br from-[#14101c] to-[#0b0811] border border-white/10 rounded-xl p-6 mb-6" data-testid="service-detail-header">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "#461d8a25" }}>
              <Briefcase className="w-6 h-6" style={{ color: "#a78bfa" }} />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black">{svc.name}</h1>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${statusCfg.cls}`}>{statusCfg.label}</span>
                {svc.category && <span className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-slate-400">{svc.category}</span>}
                {svc.price && <span className="text-[10px] font-bold text-[#f7c935]">{svc.price}</span>}
              </div>
            </div>
          </div>
          <button onClick={onAgentToggle}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold transition-all ${agentVisible ? "bg-[#f7c935] text-[#14101c]" : "bg-[#14101c] border border-[#f7c935]/30 text-[#f7c935]"}`}>
            <Bot className="w-3.5 h-3.5" /> Expert
          </button>
        </div>

        {/* Progress */}
        {phases.length > 0 && (
          <div className="mb-2">
            <div className="flex items-center justify-between mb-1.5 text-xs">
              <span className="text-slate-500 uppercase tracking-wider font-bold">Avancement global</span>
              <span className="font-bold text-[#f7c935]">{progress}% · {bundle.phases_done}/{bundle.phases_total} phases</span>
            </div>
            <div className="h-2 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#f7c935] to-[#a78bfa]" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        {svc.description && <p className="text-sm text-slate-400 mt-4">{svc.description}</p>}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[#14101c] border border-white/5 rounded-xl p-1 mb-4 overflow-x-auto" data-testid="service-tabs">
        {[
          { key: "timeline", icon: Target, label: `Timeline${phases.length ? ` (${phases.length})` : ""}` },
          { key: "documents", icon: FileSignature, label: "Documents" },
          { key: "invoices", icon: Receipt, label: `Factures${bundle.invoices?.length ? ` (${bundle.invoices.length})` : ""}` },
          { key: "accompaniment", icon: FolderOpen, label: "Accompagnement" },
          { key: "comments", icon: MessageSquare, label: `Echanges${comments.length ? ` (${comments.length})` : ""}` },
        ].map(t => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${active ? "bg-[#f7c935]/15 text-[#f7c935]" : "text-slate-400 hover:bg-white/5"}`}
              data-testid={`service-tab-${t.key}`}>
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-6">
        {tab === "timeline" && (
          <TimelinePanel phases={phases} />
        )}

        {tab === "documents" && (
          <DocumentsPanel
            service={svc}
            existingIntake={existingIntake}
            onOpenIntake={openIntake}
            onViewForm={setViewingForm}
          />
        )}

        {tab === "invoices" && (
          <InvoicesPanel invoices={bundle.invoices || []} />
        )}

        {tab === "accompaniment" && (
          <AccompanimentPanel accompaniment={bundle.accompaniment} />
        )}

        {tab === "comments" && (
          <CommentsPanel
            comments={comments}
            user={user}
            newComment={newComment}
            setNewComment={setNewComment}
            onPost={postComment}
            sending={sending}
            endRef={commentsEndRef}
          />
        )}
      </div>

      {/* Intake form dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl font-bold">{activeTemplate?.name || "Document"}</DialogTitle></DialogHeader>
          {activeTemplate && (
            <div className="space-y-6 mt-4">
              {(activeTemplate.sections || []).map((section, si) => (
                <div key={si}>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-3">{section.title || section.section_title}</h3>
                  <div className="space-y-3">
                    {(section.fields || []).map((field, fi) => {
                      const key = `${section.title || section.section_title}_${field.label || field.field_name}`;
                      return (
                        <div key={fi}>
                          <label className="text-sm text-slate-300 mb-1 block">
                            {field.label || field.field_name} {field.required && <span className="text-red-400">*</span>}
                          </label>
                          {field.type === "textarea" ? (
                            <Textarea value={formResponses[key] || ""} onChange={e => setFormResponses({ ...formResponses, [key]: e.target.value })}
                              className={inputCls} rows={3} />
                          ) : field.type === "select" && field.options?.length > 0 ? (
                            <select value={formResponses[key] || ""} onChange={e => setFormResponses({ ...formResponses, [key]: e.target.value })}
                              className="w-full bg-[#161020] border border-white/10 text-white rounded-lg px-3 py-2 text-sm">
                              <option value="">Choisir...</option>
                              {field.options.map((opt, oi) => <option key={oi} value={opt}>{opt}</option>)}
                            </select>
                          ) : (
                            <Input type={field.type || "text"} value={formResponses[key] || ""}
                              onChange={e => setFormResponses({ ...formResponses, [key]: e.target.value })}
                              className={inputCls} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              <Button onClick={submitIntake} disabled={submitting} className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="submit-intake-form">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Soumettre
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View filled form */}
      <Dialog open={!!viewingForm} onOpenChange={(v) => !v && setViewingForm(null)}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl font-bold">{viewingForm?.template_name}</DialogTitle></DialogHeader>
          {viewingForm && (
            <div className="space-y-4 mt-4">
              <div className="text-xs text-slate-500">Soumis le {new Date(viewingForm.created_at).toLocaleDateString("fr")}</div>
              {Object.entries(viewingForm.responses || {}).map(([k, v]) => (
                <div key={k} className="bg-[#161020] rounded-lg p-3">
                  <div className="text-xs text-slate-500 mb-1">{k}</div>
                  <div className="text-sm text-white">{String(v) || "-"}</div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {agentVisible && (
        <div className="fixed bottom-6 right-6 w-[420px] z-50 shadow-2xl shadow-black/50" data-testid="floating-chat-widget">
          <AgentChat agentType="conseiller" agentName="Expert Strategie" agentColor="#f7c935"
            context={{ service: svc.name }} onClose={onAgentToggle} />
        </div>
      )}
    </div>
  );
}


// ─── Sub-panels ───────────────────────────────────────────────
function TimelinePanel({ phases }) {
  if (!phases.length) {
    return <div className="text-center py-10 text-slate-500">
      <Target className="w-10 h-10 mx-auto mb-3 text-slate-600" />
      <p className="text-sm">Aucune phase definie. Votre consultant ajoutera bientot un plan de progression.</p>
    </div>;
  }
  const sorted = [...phases].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return (
    <div className="relative" data-testid="timeline-panel">
      <div className="absolute left-4 top-4 bottom-4 w-px bg-white/10" />
      <div className="space-y-5">
        {sorted.map((p) => {
          const icon = p.status === "done" ? CheckCircle2 : p.status === "in_progress" ? Clock : Circle;
          const iconColor = p.status === "done" ? "#10b981" : p.status === "in_progress" ? "#f7c935" : "#64748b";
          const Icon = icon;
          return (
            <div key={p.phase_id} className="relative flex gap-4 pl-0" data-testid={`phase-${p.phase_id}`}>
              <div className="relative w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-[#0b0811] border-2 border-white/10" style={{ borderColor: iconColor + "40" }}>
                <Icon className="w-4 h-4" style={{ color: iconColor }} />
              </div>
              <div className="flex-1 pb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-bold text-white">{p.title}</h4>
                  {p.status === "done" && <span className="text-[9px] px-1.5 py-0.5 rounded bg-green-500/15 text-green-400 font-bold uppercase">Termine</span>}
                  {p.status === "in_progress" && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#f7c935]/15 text-[#f7c935] font-bold uppercase">En cours</span>}
                </div>
                {p.description && <p className="text-xs text-slate-400 mt-1">{p.description}</p>}
                {p.due_date && (
                  <div className="flex items-center gap-1 text-[10px] text-slate-500 mt-1">
                    <CalIcon className="w-2.5 h-2.5" /> Echeance: {p.due_date}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DocumentsPanel({ service, existingIntake, onOpenIntake, onViewForm }) {
  const needsIntake = service.intake_form_template_id && !existingIntake;
  return (
    <div className="space-y-3" data-testid="documents-panel">
      {!service.intake_form_template_id && (
        <div className="text-center py-8 text-slate-500">
          <FileSignature className="w-10 h-10 mx-auto mb-3 text-slate-600" />
          <p className="text-sm">Aucun document requis pour ce service.</p>
        </div>
      )}
      {needsIntake && (
        <div className="bg-gradient-to-r from-red-500/10 to-transparent border border-red-500/20 rounded-xl p-4" data-testid="intake-required">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-bold text-white mb-1">Document d'intake a remplir</h4>
              <p className="text-xs text-slate-400 mb-3">Votre consultant a besoin de ces informations pour lancer votre accompagnement.</p>
              <Button onClick={onOpenIntake} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="fill-intake-btn">
                <FileText className="w-3.5 h-3.5 mr-1.5" /> Remplir maintenant
              </Button>
            </div>
          </div>
        </div>
      )}
      {existingIntake && (
        <div className="bg-green-500/5 border border-green-500/20 rounded-xl p-4 flex items-center gap-3" data-testid="intake-filled">
          <Check className="w-5 h-5 text-green-400" />
          <div className="flex-1">
            <h4 className="font-bold text-white text-sm">{existingIntake.template_name}</h4>
            <p className="text-[11px] text-slate-500">Soumis le {new Date(existingIntake.created_at).toLocaleDateString("fr")}</p>
          </div>
          <Button onClick={() => onViewForm(existingIntake)} variant="outline" className="border-white/10 text-white hover:bg-white/5 text-xs h-8" data-testid="view-intake-btn">
            Voir
          </Button>
        </div>
      )}
    </div>
  );
}

function InvoicesPanel({ invoices }) {
  if (!invoices.length) {
    return <div className="text-center py-8 text-slate-500">
      <Receipt className="w-10 h-10 mx-auto mb-3 text-slate-600" />
      <p className="text-sm">Aucune facture liee a ce service.</p>
    </div>;
  }
  return (
    <div className="space-y-2" data-testid="invoices-panel">
      {invoices.map(inv => (
        <div key={inv.invoice_id} className="bg-[#0b0811] border border-white/5 rounded-lg p-3 flex items-center gap-3" data-testid={`invoice-${inv.invoice_id}`}>
          <Receipt className="w-4 h-4 text-[#f7c935]" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white">{inv.number || inv.invoice_id}</div>
            <div className="text-[10px] text-slate-500">{inv.date || inv.created_at?.slice(0, 10)}</div>
          </div>
          <div className="text-sm font-bold text-[#f7c935]">{inv.total_ttc || inv.amount} EUR</div>
        </div>
      ))}
    </div>
  );
}

function AccompanimentPanel({ accompaniment }) {
  if (!accompaniment) {
    return <div className="text-center py-8 text-slate-500">
      <FolderOpen className="w-10 h-10 mx-auto mb-3 text-slate-600" />
      <p className="text-sm">Aucun accompagnement lie a ce service.</p>
    </div>;
  }
  return (
    <div className="bg-[#0b0811] border border-white/5 rounded-xl p-4" data-testid="accompaniment-panel">
      <div className="flex items-center gap-3 mb-2">
        <FolderOpen className="w-5 h-5 text-[#f7c935]" />
        <div>
          <h4 className="font-bold text-white">{accompaniment.title}</h4>
          <p className="text-[11px] text-slate-500">{accompaniment.formula}</p>
        </div>
      </div>
      {accompaniment.description && <p className="text-xs text-slate-400 mt-2">{accompaniment.description}</p>}
    </div>
  );
}

function CommentsPanel({ comments, user, newComment, setNewComment, onPost, sending, endRef }) {
  return (
    <div className="flex flex-col h-[400px]" data-testid="comments-panel">
      <div className="flex-1 overflow-y-auto space-y-3 pr-2 -mr-2 mb-3">
        {comments.length === 0 && (
          <div className="text-center py-10 text-slate-500">
            <MessageSquare className="w-10 h-10 mx-auto mb-3 text-slate-600" />
            <p className="text-sm">Aucun echange pour le moment.</p>
            <p className="text-xs text-slate-600 mt-1">Envoyez un message a votre consultant.</p>
          </div>
        )}
        {comments.map(c => {
          const isMe = c.author_id === user?.user_id;
          const isStaff = ["admin", "coach", "sous_traitant"].includes(c.author_role);
          return (
            <div key={c.comment_id} className={`flex gap-2 ${isMe ? "flex-row-reverse" : ""}`} data-testid={`comment-${c.comment_id}`}>
              <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: isStaff ? "#461d8a30" : "#f7c93530" }}>
                <User className="w-3.5 h-3.5" style={{ color: isStaff ? "#a78bfa" : "#f7c935" }} />
              </div>
              <div className={`max-w-[75%] ${isMe ? "text-right" : ""}`}>
                <div className={`inline-block rounded-xl p-3 text-sm ${isMe ? "bg-[#f7c935] text-[#14101c]" : "bg-[#0b0811] border border-white/5 text-white"}`}>
                  <div className="text-[10px] font-bold opacity-70 mb-0.5">{c.author_name}{isStaff && <span className="ml-1 opacity-60">· Consultant Blitzz</span>}</div>
                  <div className="whitespace-pre-wrap break-words">{c.body}</div>
                </div>
                <div className="text-[9px] text-slate-600 mt-1 px-1">{new Date(c.created_at).toLocaleString("fr")}</div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="flex gap-2 border-t border-white/5 pt-3">
        <Textarea value={newComment} onChange={e => setNewComment(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && e.metaKey) onPost(); }}
          placeholder="Ecrire un message... (Cmd+Enter pour envoyer)"
          rows={2} className={`${inputCls} flex-1 resize-none text-sm`} data-testid="new-comment-input" />
        <Button onClick={onPost} disabled={sending || !newComment.trim()} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold self-stretch" data-testid="send-comment-btn">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}
